import { X, Loader2, RefreshCw } from 'lucide-react';
import { useState, useEffect } from 'react';
import { customersService, SubscriptionFrequency } from '../../services/customers.service';
import { customerTypesService, CustomerType } from '../../services/customerTypes.service';
import { subscriptionPlansService, SubscriptionPlan } from '../../services/subscriptionPlans.service';
import { useCurrency } from '../../hooks/useCurrency';
import { toast } from 'sonner';

const FREQ_LABELS: Record<string, string> = { MONTHLY: 'Monthly', QUARTERLY: 'Quarterly', ANNUALLY: 'Annually' };

function calcSubDates(startDate: string, frequency: string): { endDate: string; nextDate: string } {
  if (!startDate || !frequency) return { endDate: '', nextDate: '' };
  const d = new Date(startDate);
  if (frequency === 'MONTHLY') d.setMonth(d.getMonth() + 1);
  else if (frequency === 'QUARTERLY') d.setMonth(d.getMonth() + 3);
  else if (frequency === 'ANNUALLY') d.setFullYear(d.getFullYear() + 1);
  d.setDate(d.getDate() - 1);
  const endDate = d.toISOString().slice(0, 10);
  d.setDate(d.getDate() + 1);
  const nextDate = d.toISOString().slice(0, 10);
  return { endDate, nextDate };
}

interface AddCustomerModalProps {
  onClose: () => void;
  onSave: (data: any) => void;
}

const INPUT = 'w-full px-4 py-2.5 text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500';
const LABEL = 'block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5';

export function AddCustomerModal({ onClose, onSave }: AddCustomerModalProps) {
  const [types, setTypes] = useState<CustomerType[]>([]);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [saving, setSaving] = useState(false);
  const { formatCurrency } = useCurrency();
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    country: '',
    membershipTier: 'BRONZE' as 'BRONZE' | 'SILVER' | 'GOLD' | 'PLATINUM',
    customerTypeId: '',
    subscriptionFrequency: '' as SubscriptionFrequency | '',
    subscriptionPlanId: '',
    subscriptionStartDate: '',
    subscriptionEndDate: '',
    nextPaymentDate: '',
  });

  useEffect(() => {
    customerTypesService.getAll().then((r) => { if (r.success && r.data) setTypes(r.data); });
  }, []);

  const selectedType = types.find((t) => t.id === form.customerTypeId) ?? null;
  const isSubscriptionBased = selectedType?.isSubscriptionBased ?? false;
  const selectedPlan = plans.find((p) => p.id === form.subscriptionPlanId) ?? null;

  useEffect(() => {
    if (isSubscriptionBased && form.customerTypeId) {
      subscriptionPlansService.getByTypeId(form.customerTypeId)
        .then((r) => { if (r.success && r.data) setPlans(r.data.filter((p) => p.isActive)); })
        .catch(() => setPlans([]));
    } else {
      setPlans([]);
    }
  }, [form.customerTypeId, isSubscriptionBased]);

  // Auto-fill frequency when plan is selected
  useEffect(() => {
    const plan = plans.find((p) => p.id === form.subscriptionPlanId);
    if (plan?.defaultFrequency) {
      setForm((f) => {
        const { endDate, nextDate } = calcSubDates(f.subscriptionStartDate, plan.defaultFrequency!);
        return { ...f, subscriptionFrequency: plan.defaultFrequency!, subscriptionEndDate: endDate, nextPaymentDate: nextDate };
      });
    }
  }, [form.subscriptionPlanId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-calculate end date and next payment when start date or frequency changes
  useEffect(() => {
    if (!form.subscriptionStartDate || !form.subscriptionFrequency) return;
    const { endDate, nextDate } = calcSubDates(form.subscriptionStartDate, form.subscriptionFrequency);
    setForm((f) => ({ ...f, subscriptionEndDate: endDate, nextPaymentDate: nextDate }));
  }, [form.subscriptionStartDate, form.subscriptionFrequency]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (field: string, value: any) => setForm((f) => ({ ...f, [field]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim()) {
      toast.error('First name, last name and email are required.');
      return;
    }
    setSaving(true);
    try {
      const dto: any = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        phone: form.phone || undefined,
        address: form.address || undefined,
        city: form.city || undefined,
        country: form.country || undefined,
        membershipTier: form.membershipTier,
        customerTypeId: form.customerTypeId || undefined,
      };
      if (isSubscriptionBased && form.subscriptionFrequency) {
        dto.subscriptionFrequency = form.subscriptionFrequency;
        dto.subscriptionPlanId = form.subscriptionPlanId || undefined;
        dto.subscriptionStartDate = form.subscriptionStartDate || undefined;
        dto.subscriptionEndDate = form.subscriptionEndDate || undefined;
        dto.nextPaymentDate = form.nextPaymentDate || undefined;
      }
      const res = await customersService.createCustomer(dto);
      if (res.success) {
        toast.success('Customer added successfully');
        onSave(res.data);
        onClose();
      } else {
        toast.error('Failed to add customer');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to add customer');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-800">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Add New Customer</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Create a new customer profile</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Basic Info */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Basic Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={LABEL}>First Name *</label>
                  <input type="text" value={form.firstName} onChange={(e) => set('firstName', e.target.value)} required className={INPUT} placeholder="First name" />
                </div>
                <div>
                  <label className={LABEL}>Last Name *</label>
                  <input type="text" value={form.lastName} onChange={(e) => set('lastName', e.target.value)} required className={INPUT} placeholder="Last name" />
                </div>
                <div>
                  <label className={LABEL}>Email *</label>
                  <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} required className={INPUT} placeholder="email@example.com" />
                </div>
                <div>
                  <label className={LABEL}>Phone</label>
                  <input type="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)} className={INPUT} placeholder="+254 7xx xxx xxx" />
                </div>
                <div>
                  <label className={LABEL}>Address</label>
                  <input type="text" value={form.address} onChange={(e) => set('address', e.target.value)} className={INPUT} placeholder="Street address" />
                </div>
                <div>
                  <label className={LABEL}>City</label>
                  <input type="text" value={form.city} onChange={(e) => set('city', e.target.value)} className={INPUT} placeholder="City" />
                </div>
                <div>
                  <label className={LABEL}>Membership Tier</label>
                  <select value={form.membershipTier} onChange={(e) => set('membershipTier', e.target.value)} className={INPUT}>
                    <option value="BRONZE">Bronze</option>
                    <option value="SILVER">Silver</option>
                    <option value="GOLD">Gold</option>
                    <option value="PLATINUM">Platinum</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Customer Type */}
            <div className="pt-4 border-t border-gray-100 dark:border-gray-800">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Customer Type</h3>
              <div>
                <label className={LABEL}>Type</label>
                <select
                  value={form.customerTypeId}
                  onChange={(e) => set('customerTypeId', e.target.value)}
                  className={INPUT}
                >
                  <option value=""> No type </option>
                  {types.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
                {selectedType?.isSubscriptionBased && (
                  <p className="text-xs text-indigo-500 dark:text-indigo-400 mt-1.5 flex items-center gap-1">
                    <RefreshCw className="w-3 h-3" /> This is a subscription-based type  fill in subscription details below
                  </p>
                )}
              </div>
            </div>

            {/* Subscription Fields (subscription-based only) */}
            {isSubscriptionBased && (
              <div className="pt-4 border-t border-gray-100 dark:border-gray-800">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Subscription Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  {/* Plan select — full width */}
                  <div className="col-span-2">
                    <label className={LABEL}>Subscription Plan</label>
                    <select value={form.subscriptionPlanId} onChange={(e) => set('subscriptionPlanId', e.target.value)} className={INPUT}>
                      <option value="">— Select a plan —</option>
                      {plans.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}{p.price ? ` — ${formatCurrency(p.price)}` : ''}{p.defaultFrequency ? ` / ${FREQ_LABELS[p.defaultFrequency]}` : ''}
                        </option>
                      ))}
                    </select>
                    {plans.length === 0 && (
                      <p className="text-xs text-amber-500 dark:text-amber-400 mt-1">No active plans for this type. Add plans via Customer Types.</p>
                    )}
                    {selectedPlan && (
                      <div className="mt-2 flex flex-wrap items-center gap-3 px-3 py-2 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
                        {selectedPlan.price && (
                          <span className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">{formatCurrency(selectedPlan.price)}</span>
                        )}
                        {selectedPlan.defaultFrequency && (
                          <span className="text-xs text-indigo-600 dark:text-indigo-400 font-medium">{FREQ_LABELS[selectedPlan.defaultFrequency]}</span>
                        )}
                        {selectedPlan.description && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">{selectedPlan.description}</span>
                        )}
                      </div>
                    )}
                  </div>
                  {/* Frequency — auto-filled but editable */}
                  <div>
                    <label className={LABEL}>Billing Frequency</label>
                    <select value={form.subscriptionFrequency} onChange={(e) => set('subscriptionFrequency', e.target.value)} className={INPUT}>
                      <option value="">— None —</option>
                      <option value="MONTHLY">Monthly</option>
                      <option value="QUARTERLY">Quarterly</option>
                      <option value="ANNUALLY">Annually</option>
                    </select>
                  </div>
                  {/* Start date */}
                  <div>
                    <label className={LABEL}>Start Date</label>
                    <input type="date" value={form.subscriptionStartDate} onChange={(e) => set('subscriptionStartDate', e.target.value)} className={INPUT} />
                  </div>
                  {/* End date — auto-calculated */}
                  <div>
                    <label className={LABEL}>
                      End Date
                      {form.subscriptionEndDate && <span className="ml-1 text-indigo-500 dark:text-indigo-400 font-normal text-[10px] uppercase tracking-wide">auto</span>}
                    </label>
                    <input type="date" value={form.subscriptionEndDate} onChange={(e) => set('subscriptionEndDate', e.target.value)} className={INPUT} />
                  </div>
                  {/* Next payment — auto-calculated */}
                  <div>
                    <label className={LABEL}>
                      Next Payment Date
                      {form.nextPaymentDate && <span className="ml-1 text-indigo-500 dark:text-indigo-400 font-normal text-[10px] uppercase tracking-wide">auto</span>}
                    </label>
                    <input type="date" value={form.nextPaymentDate} onChange={(e) => set('nextPaymentDate', e.target.value)} className={INPUT} />
                  </div>
                </div>
              </div>
            )}

            {/* Buttons */}
            <div className="flex items-center gap-3 pt-2">
              <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
                Cancel
              </button>
              <button type="submit" disabled={saving} className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-60">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                Add Customer
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
